import React from 'react';
import PropTypes from 'prop-types';
import { RequestTypes, SettingsNameToRequestType } from '@medflex/shared-types';
import {
  IconWidgetCalendar,
  IconWidgetQuestion,
  IconWidgetResultsInquiry,
  IconWidgetPrescription,
  IconWidgetReferral,
} from '@medflex/components/stories/Icons';
import { trackEvent } from '@medflex/utils/Tracking';
import { isEnabled as isWidgetSettingsEnabled } from '@medflex/utils/widgetSettings';
import useIsWideScreen from '@medflex/hooks/useIsWideScreen';
import { useTranslation } from 'react-i18next';
import useModal from '../../hooks/useModal';
import usePhysicianTeam from '../../hooks/usePhysicianTeam';
import usePhysicianWidgetSettings from '../../hooks/usePhysicianWidgetSettings';
import { MODALS } from '../../hooks/useIsModal';

const PatientWidgetAction = ({ physicianUserId, requestingForUserId }) => {
  const { open } = useModal();
  const { isLoading: isLoadingTeam } = usePhysicianTeam(physicianUserId);
  const { isLoading: isLoadingWidgetSettings, widgetSettings } = usePhysicianWidgetSettings(
    [physicianUserId],
    false,
  );
  const isWideScreen = useIsWideScreen();
  const { t } = useTranslation('messenger');

  const requestTypeDefinitions = {
    [RequestTypes.APPOINTMENT]: {
      label: t(`PatientWidgetAction.appointment.${isWideScreen ? 'long' : 'short'}`),
      icon: IconWidgetCalendar,
      order: 1,
    },
    [RequestTypes.REQUEST_FOLLOW_UP_PRESCRIPTION]: {
      label: t(
        `PatientWidgetAction.requestFollowUpPrescription.${isWideScreen ? 'long' : 'short'}`,
      ),
      icon: IconWidgetPrescription,
      order: 2,
    },
    [RequestTypes.GENERAL_INQUIRY]: {
      label: t(`PatientWidgetAction.generalInquiry.${isWideScreen ? 'long' : 'short'}`),
      icon: IconWidgetQuestion,
      order: 3,
    },
    [RequestTypes.FINDINGS_INQUIRY]: {
      label: t(`PatientWidgetAction.findingsInquiry.${isWideScreen ? 'long' : 'short'}`),
      icon: IconWidgetResultsInquiry,
      order: 4,
    },
    [RequestTypes.REFERRAL]: {
      label: t(`PatientWidgetAction.referral.${isWideScreen ? 'long' : 'short'}`),
      icon: IconWidgetReferral,
      order: 5,
    },
  };

  const handleOpenRequestWidget = (event, requestType) => {
    trackEvent(event);
    open({
      modal: MODALS.requestWidget,
      physicianUserId,
      requestingForUserId,
      requestType,
    });
  };

  if (
    isLoadingWidgetSettings ||
    isLoadingTeam ||
    !isWidgetSettingsEnabled(widgetSettings[physicianUserId])
  ) {
    return null;
  }

  const availableRequestTypes = Object.entries(widgetSettings[physicianUserId])
    .filter(
      ([name, { active }]) => requestTypeDefinitions[SettingsNameToRequestType[name]] && active,
    )
    .map(([name]) => SettingsNameToRequestType[name])
    .sort((a, b) => requestTypeDefinitions[a].order - requestTypeDefinitions[b].order);

  return (
    <div className="flex gap-2 overflow-x-scroll lg:flex-wrap lg:gap-4 lg:justify-center">
      {availableRequestTypes.map(requestType => {
        const Icon = requestTypeDefinitions[requestType].icon;
        return (
          <button
            key={requestType}
            className="flex flex-shrink-0 items-center rounded-full border border-blue-500 px-2 py-1 bg-blue-100 text-slate-900 font-semibold hover:text-blue-600 hover:shadow-md shadow-blue-100 lg:px-4"
            onClick={event => handleOpenRequestWidget(event, requestType)}
            type="button"
          >
            <Icon className="h-6 w-6 lg:h-8 lg:w-8" />
            <span className="ml-2 text-sm lg:text-base">
              {requestTypeDefinitions[requestType].label}
            </span>
          </button>
        );
      })}
    </div>
  );
};

PatientWidgetAction.propTypes = {
  physicianUserId: PropTypes.string.isRequired,
  requestingForUserId: PropTypes.string.isRequired,
};

export default PatientWidgetAction;
